import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, Mail, Phone, Calendar, Users, ArrowLeft, Download, LogOut, Lock, ArrowDownUp, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import AdminVenuesPanel from "@/components/AdminVenuesPanel";
import AdminSettingsPanel from "@/components/AdminSettingsPanel";
import AdminImagesPanel from "@/components/AdminImagesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pp_admin_token";

export default function AdminInquiries() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState("desc"); // desc = newest first

  const authed = !!token;
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [i, n] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
      ]);
      setInquiries(i.data || []);
      setSubscribers(n.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUsername(""); setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries([]); setSubscribers([]);
  };

  const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return d; } };

  const sorted = (arr) => {
    const copy = [...arr];
    copy.sort((a, b) => {
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return order === "desc" ? db - da : da - db;
    });
    return copy;
  };

  const downloadXLSX = () => {
    const wb = XLSX.utils.book_new();

    const inqRows = sorted(inquiries).map((q, idx) => ({
      "#": idx + 1,
      "Received At": fmt(q.created_at),
      "Name": q.name || "",
      "Email": q.email || "",
      "Phone": q.phone || "",
      "Event Type": q.event_type || "",
      "City": q.city || "",
      "Event Date": q.event_date || "",
      "Guest Count": q.guest_count || "",
      "Budget": q.budget_range || "",
      "Venue Booked": q.venue_booked || "",
      "Services Required": (q.services_required || []).join(", "),
      "Message": q.message || "",
      "Source": q.source || "website",
      "ID": q.id,
    }));
    const wsInq = XLSX.utils.json_to_sheet(inqRows);
    wsInq["!cols"] = [
      { wch: 4 }, { wch: 22 }, { wch: 22 }, { wch: 28 }, { wch: 18 },
      { wch: 20 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 32 }, { wch: 50 }, { wch: 14 }, { wch: 38 },
    ];
    XLSX.utils.book_append_sheet(wb, wsInq, "Inquiries");

    const subRows = sorted(subscribers).map((s, idx) => ({
      "#": idx + 1,
      "Email": s.email,
      "Subscribed At": fmt(s.created_at),
      "ID": s.id,
    }));
    const wsSub = XLSX.utils.json_to_sheet(subRows);
    wsSub["!cols"] = [{ wch: 4 }, { wch: 32 }, { wch: 22 }, { wch: 38 }];
    XLSX.utils.book_append_sheet(wb, wsSub, "Newsletter");

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `petals-and-promises-leads-${stamp}.xlsx`);
    toast.success("Export ready.");
  };

  // ============== LOGIN VIEW ==============
  if (!authed) {
    return (
      <div data-testid="page-admin-login" className="bg-[#FAF6F1] min-h-screen pt-28 md:pt-32 pb-20 flex items-start justify-center">
        <div className="w-full max-w-md mx-auto px-6">
          <Link to="/" className="link-gold text-[12px] tracking-[0.18em] uppercase text-[#B65980] inline-flex font-semibold">
            <ArrowLeft size={14}/> Back to site
          </Link>
          <div className="mt-8 lux-card p-8 md:p-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F4DCD8]/60 text-[#B65980] mb-5">
              <Lock size={22} />
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#3B312D] leading-tight">
              Admin <span className="italic text-[#B65980]">Sign In</span>
            </h1>
            <p className="mt-3 text-[14px] text-[#3B312D]/75 leading-relaxed">
              Enter the admin credentials to view inquiries and newsletter subscribers.
            </p>
            <form onSubmit={login} className="mt-7 space-y-4" data-testid="admin-login-form">
              <input
                type="text" required placeholder="Username"
                value={username} onChange={(e) => setUsername(e.target.value)}
                className="lux-input" data-testid="admin-login-username" autoComplete="username"
              />
              <input
                type="password" required placeholder="Password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="lux-input" data-testid="admin-login-password" autoComplete="current-password"
              />
              <button type="submit" disabled={loggingIn} className="btn-primary w-full justify-center" data-testid="admin-login-submit">
                {loggingIn ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
                {loggingIn ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ============== DASHBOARD VIEW ==============
  const inqSorted = sorted(inquiries);
  const subSorted = sorted(subscribers);

  return (
    <div data-testid="page-admin" className="bg-[#FAF6F1] min-h-screen pt-28 md:pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/" className="link-gold text-[12px] tracking-[0.18em] uppercase text-[#B65980] inline-flex font-semibold">
            <ArrowLeft size={14}/> Back to site
          </Link>
          <button onClick={logout} className="text-[12px] tracking-[0.18em] uppercase text-[#3B312D]/70 hover:text-[#B65980] inline-flex items-center gap-2 font-semibold" data-testid="admin-logout">
            <LogOut size={14}/> Sign out
          </button>
        </div>

        <div className="mt-6 flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="pill">Admin · Lead Inbox</p>
            <h1 className="font-display text-4xl md:text-6xl mt-4 text-[#3B312D] leading-[1.05]">
              Who's <span className="italic text-[#B65980]">reached out</span>.
            </h1>
            <p className="mt-3 text-[14px] text-[#3B312D]/75 max-w-xl">
              Every inquiry and newsletter signup from your site. Download everything as XLSX or toggle sort order.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setOrder(order === "desc" ? "asc" : "desc")} data-testid="admin-sort-toggle" className="btn-ghost">
              <ArrowDownUp size={14} /> {order === "desc" ? "Newest first" : "Oldest first"}
            </button>
            <button onClick={load} disabled={loading} className="btn-ghost" data-testid="admin-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Loading…" : "Refresh"}
            </button>
            <button onClick={downloadXLSX} className="btn-primary" data-testid="admin-download-xlsx">
              <Download size={14} /> Download XLSX
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => setTab("inquiries")} data-testid="admin-tab-inquiries"
            className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase font-semibold border transition ${tab === "inquiries" ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"}`}>
            Inquiries ({inquiries.length})
          </button>
          <button onClick={() => setTab("subscribers")} data-testid="admin-tab-subscribers"
            className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase font-semibold border transition ${tab === "subscribers" ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"}`}>
            Newsletter ({subscribers.length})
          </button>
          <button onClick={() => setTab("venues")} data-testid="admin-tab-venues"
            className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase font-semibold border transition ${tab === "venues" ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"}`}>
            Venues
          </button>
          <button onClick={() => setTab("images")} data-testid="admin-tab-images"
            className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase font-semibold border transition ${tab === "images" ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"}`}>
            Images
          </button>
          <button onClick={() => setTab("settings")} data-testid="admin-tab-settings"
            className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase font-semibold border transition ${tab === "settings" ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"}`}>
            Settings
          </button>
        </div>

        {tab === "inquiries" && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {inqSorted.length === 0 && <p className="text-[#3B312D]/60 py-12">No inquiries yet.</p>}
            {inqSorted.map((q) => (
              <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className="lux-card p-7">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="font-display text-2xl text-[#3B312D]">{q.name}</h3>
                    <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mt-1">
                      {q.event_type || "—"} {q.city ? `· ${q.city}` : ""}
                    </p>
                  </div>
                  <p className="text-[11px] text-[#3B312D]/60 whitespace-nowrap">{fmt(q.created_at)}</p>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13.5px] text-[#3B312D]">
                  <a href={`mailto:${q.email}`} className="flex items-center gap-2 link-gold w-fit break-all"><Mail size={13} className="text-[#B65980] shrink-0"/>{q.email}</a>
                  <a href={`tel:${q.phone}`} className="flex items-center gap-2 link-gold w-fit"><Phone size={13} className="text-[#B65980] shrink-0"/>{q.phone}</a>
                  {q.event_date && <div className="flex items-center gap-2"><Calendar size={13} className="text-[#B65980]"/>{q.event_date}</div>}
                  {q.guest_count && <div className="flex items-center gap-2"><Users size={13} className="text-[#B65980]"/>{q.guest_count}</div>}
                  {q.budget_range && <div><span className="text-[#8A9A7B]">Budget:</span> {q.budget_range}</div>}
                  {q.venue_booked && <div><span className="text-[#8A9A7B]">Venue:</span> {q.venue_booked}</div>}
                </div>
                {q.services_required?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mb-2">Services</p>
                    <div className="flex flex-wrap gap-1.5">
                      {q.services_required.map((s) => (
                        <span key={s} className="px-3 py-1 rounded-full text-[11px] bg-[#F4DCD8]/60 text-[#B65980] border border-[#B65980]/20">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {q.message && (
                  <div className="mt-4 pt-4 border-t border-[rgba(59,49,45,0.08)]">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mb-2">Message</p>
                    <p className="text-[14px] text-[#3B312D]/85 leading-relaxed">{q.message}</p>
                  </div>
                )}
                <p className="mt-4 text-[10px] text-[#3B312D]/40">Source: {q.source || "website"} · ID {q.id.slice(0, 8)}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "subscribers" && (
          <div className="mt-8 lux-card p-0 overflow-hidden">
            {subSorted.length === 0 ? (
              <p className="text-[#3B312D]/60 p-8">No subscribers yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-[#F4DCD8]/40 text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold">
                    <tr>
                      <th className="px-6 py-4">#</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(59,49,45,0.08)]">
                    {subSorted.map((s, i) => (
                      <tr key={s.id} data-testid={`admin-sub-${s.id}`} className="hover:bg-[#FAF6F1]">
                        <td className="px-6 py-4 text-[#3B312D]/60">{i + 1}</td>
                        <td className="px-6 py-4 text-[#3B312D]">
                          <a href={`mailto:${s.email}`} className="hover:text-[#B65980]">{s.email}</a>
                        </td>
                        <td className="px-6 py-4 text-[#3B312D]/75">{fmt(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {tab === "venues" && (
          <div className="mt-8" data-testid="admin-venues-tab">
            <AdminVenuesPanel token={token} />
          </div>
        )}

        {tab === "images" && (
          <div className="mt-8" data-testid="admin-images-tab">
            <AdminImagesPanel token={token} />
          </div>
        )}

        {tab === "settings" && (
          <div className="mt-8" data-testid="admin-settings-tab">
            <AdminSettingsPanel token={token} />
          </div>
        )}
      </div>
    </div>
  );
}
